import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Avatar, Skeleton } from '@mui/material';
import { Check } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { stringToColor } from '../../../shared/commonFunctions';
import { USER_ROLES } from '../../../shared/appConstants';
import OverflowTooltip from '../../../shared/OverflowTooltip';

import DayPlanTable from '../components/DayPlanTable';
import WorklogStrip from '../components/WorklogStrip';
import BlockersRisks from '../components/BlockersRisks';
import AdditionalNotes from '../components/AdditionalNotes';
import {
  fetchDailyStandupLog,
  fetchDailyStandupTeamMembers,
  addDailyStandupNote,
  updateDailyStandupNote,
  deleteDailyStandupNote,
} from '../../../store/dailyStandupSlice';

import './LogStandupTab.scss';


const LogStandupContentSkeleton = () => (
  <Box className="content-sections">
    {/* Day Plan Table x2 */}
    {[0, 1].map(tableIdx => (
      <Box key={tableIdx} className="skeleton-section-block">
        <Skeleton variant="text" width={180} height={26} className="skeleton-section-title" />
        <Box className="skeleton-table-wrapper">
          <Box className="skeleton-table-header">
            {[140, 100, 80, 60].map((w, i) => <Skeleton key={i} variant="text" width={w} height={16} />)}
          </Box>
          {[1, 2, 3].map(row => (
            <Box key={row} className="skeleton-table-row">
              <Skeleton variant="circular" width={28} height={28} />
              <Skeleton variant="text" width={130} height={16} />
              <Skeleton variant="text" width={90} height={16} className="skeleton-ml-auto" />
              <Skeleton variant="rectangular" width={60} height={22} className="skeleton-chip" />
            </Box>
          ))}
        </Box>
      </Box>
    ))}

    {/* Worklog strip */}
    <Box className="skeleton-section-block">
      <Skeleton variant="text" width={110} height={26} className="skeleton-section-title" />
      <Box className="skeleton-worklog-strip">
        {[1, 2, 3, 4, 5, 6, 7].map(i => (
          <Skeleton key={i} variant="rectangular" width={72} height={68} className="skeleton-worklog-card" />
        ))}
      </Box>
    </Box>

    {/* Blockers & Risks */}
    <Box className="skeleton-section-block">
      <Skeleton variant="text" width={150} height={26} className="skeleton-section-title" />
      {[1, 2].map(i => (
        <Box key={i} className="skeleton-list-row">
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="text" width={210} height={16} />
          <Skeleton variant="rectangular" width={70} height={22} className="skeleton-chip skeleton-ml-auto" />
        </Box>
      ))}
    </Box>

    {/* Additional Notes */}
    <Box>
      <Skeleton variant="text" width={140} height={26} className="skeleton-section-title" />
      {[1, 2].map(i => (
        <Box key={i} className="skeleton-notes-card">
          <Skeleton variant="text" width="88%" height={16} />
          <Skeleton variant="text" width="55%" height={16} />
        </Box>
      ))}
    </Box>
  </Box>
);

const LogStandupSkeleton = ({ showTeamSelector = false }) => (
  <Box className="log-standup-tab-container skeleton-wrapper">
    {/* Developer selector skeleton */}
    {showTeamSelector && (
      <Box className="developer-selector-container">
        <Skeleton variant="text" width={100} height={20} className="skeleton-section-title" />
        <Box className="developer-chips">
          {[1, 2, 3, 4].map(i => (
            <Box key={i} className="developer-chip skeleton-chip-disabled">
              <Skeleton variant="circular" width={24} height={24} />
              <Skeleton variant="text" width={60} height={18} />
            </Box>
          ))}
        </Box>
      </Box>
    )}
    <LogStandupContentSkeleton />
  </Box>
);

const formatName = (fullName) => {
  if (!fullName) return '';
  const parts = fullName.trim().split(' ');
  let formatted = parts[0];
  if (parts.length > 1 && parts[1]) {
    formatted += ` ${parts[1][0]}`;
  }
  return formatted;
};

const DeveloperSelector = ({ teamMembers = [], selectedUserId, onSelectUser, disabled }) => {
  const { t } = useTranslation();

  if (!teamMembers || teamMembers.length === 0) return null;

  return (
    <Box className="developer-selector-container">
      <Typography variant="subtitle1" className="section-label">
        {t('dailyStandup.logStandup.selectTeam')}
      </Typography>
      <Box className="developer-chips">
        {teamMembers.map((member) => {
          const isSelected = selectedUserId === member.id;
          return (
            <Box
              key={member.id}
              className={`developer-chip ${isSelected ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
              onClick={() => {
                if (!disabled) {
                  onSelectUser(member.id);
                }
              }}
            >
              <Avatar
                src={member.profile_picture || null}
                className="developer-avatar"
                style={{ backgroundColor: stringToColor(member.name || '') }}
              >
                {member.avatar}
              </Avatar>
              <OverflowTooltip
                title={member.name}
                placement="top"
                className="developer-name"
              >
                {formatName(member.name)}
              </OverflowTooltip>
              {isSelected && <Check className="check-icon" />}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const pickCurrentUser = (user, fallback = null) => {
  if (!user) return fallback;
  return {
    id: user.id,
    name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.name || 'Current User',
    avatar: user.avatar || user.initials || 'CU',
    profile_picture: user.profile_picture || user.profilePicture || null,
  };
};

const LogStandupTab = ({ selectedProjects, refreshKey = 0 }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const authUser = useSelector((state) => state.auth?.user) || JSON.parse(localStorage.getItem('userDetails') || '{}');
  const {
    teamMembers,
    teamMembersLoading,
    teamMembersError,
    standupLog,
    standupLogLoading,
    standupLogError,
  } = useSelector((state) => state.dailyStandup || {});
  const { projectDropdownsByUserLoading, projectDropdownsByUserError } = useSelector((state) => state.tickets || {});

  const primaryProjectId = selectedProjects?.[0] || null;
  const selectedProjectIds = useMemo(
    () => (Array.isArray(selectedProjects) ? selectedProjects.filter(Boolean) : []),
    [selectedProjects]
  );

  const currentUser = useMemo(() => pickCurrentUser(authUser), [authUser]);
  const [selectedUserId, setSelectedUserId] = useState(currentUser?.id || '');
  const [worklogFilter, setWorklogFilter] = useState('today');
  const [isRefreshingOrSwitching, setIsRefreshingOrSwitching] = useState(true);
  const [optimisticNotes, setOptimisticNotes] = useState(null);
  const [isNoteUpdating, setIsNoteUpdating] = useState(false);
  const prevSelectedUserIdRef = useRef(selectedUserId);
  const prevRefreshKeyRef = useRef(refreshKey);
  const prevSelectedProjectIdsRef = useRef(selectedProjectIds.join(','));

  useEffect(() => {
    const isUserSwitch = selectedUserId && prevSelectedUserIdRef.current !== selectedUserId;
    const isRefresh = refreshKey !== prevRefreshKeyRef.current;
    const currentProjectIdsStr = selectedProjectIds.join(',');
    const isProjectSwitch = currentProjectIdsStr !== prevSelectedProjectIdsRef.current;

    if (isUserSwitch || isRefresh || isProjectSwitch) {
      setIsRefreshingOrSwitching(true);
      prevSelectedUserIdRef.current = selectedUserId;
      prevRefreshKeyRef.current = refreshKey;
      prevSelectedProjectIdsRef.current = currentProjectIdsStr;
    }
  }, [selectedUserId, refreshKey, selectedProjectIds]);

  // Removed standupLogLoading useEffect to avoid race conditions

  const canViewTeamSelector = [USER_ROLES.ADMIN, USER_ROLES.PM].includes(String(authUser?.role || '').toLowerCase());
  const visibleTeamMembers = useMemo(() => {
    if (!canViewTeamSelector) return [];
    const members = Array.isArray(teamMembers) ? teamMembers : [];

    if (currentUser?.id && !members.some((member) => String(member.id) === String(currentUser.id))) {
      return [currentUser, ...members];
    }

    return members.length > 0 ? members : (currentUser?.id ? [currentUser] : []);
  }, [canViewTeamSelector, currentUser, teamMembers]);

  useEffect(() => {
    if (currentUser?.id) {
      setSelectedUserId(currentUser.id);
    }
  }, [currentUser?.id]);

  useEffect(() => {
    if (!selectedProjectIds.length) return;
    if (canViewTeamSelector) {
      dispatch(fetchDailyStandupTeamMembers({ projectIds: selectedProjectIds }));
    }
  }, [dispatch, selectedProjectIds, canViewTeamSelector]);

  useEffect(() => {
    if (!visibleTeamMembers.length) return;

    const selectedStillExists = visibleTeamMembers.some((member) => String(member.id) === String(selectedUserId));
    if (!selectedStillExists) {
      setSelectedUserId(visibleTeamMembers[0]?.id || currentUser?.id || '');
    }
  }, [currentUser?.id, selectedUserId, visibleTeamMembers]);

  useEffect(() => {
    if (!selectedProjectIds.length || !selectedUserId) return;

    dispatch(fetchDailyStandupLog({
      projectIds: selectedProjectIds.join(','),
      userId: selectedUserId,
      worklogFilter,
    })).finally(() => {
      setIsRefreshingOrSwitching(false);
    });
  }, [dispatch, selectedProjectIds, selectedUserId, worklogFilter, refreshKey]);

  useEffect(() => {
    const error = projectDropdownsByUserError || teamMembersError || standupLogError;
    if (error) {
      toast.error(typeof error === 'string' ? error : t('common.error'));
    }
  }, [projectDropdownsByUserError, teamMembersError, standupLogError, t]);

  const data = standupLog || null;
  
  useEffect(() => {
    // Reset optimistic state whenever fresh data comes from backend
    setOptimisticNotes(null);
  }, [data]);

  const yesterdayProjects = data?.yesterday_plan || data?.completed_yesterday || [];
  const todayProjects = data?.today_plan || [];
  const flatBlockers = useMemo(() => {
    if (!Array.isArray(data?.blockers)) return [];
    return data.blockers.flatMap((projectBlocker) => projectBlocker?.tickets || []);
  }, [data?.blockers]);
  const notes = data?.additional_notes || data?.notes || [];
  const displayNotes = optimisticNotes !== null ? optimisticNotes : notes;
  const worklogData = data?.worklog || [];

  const isViewingOwnStandup = !selectedUserId || String(selectedUserId) === String(currentUser?.id);
  const isBusy = projectDropdownsByUserLoading || teamMembersLoading || standupLogLoading;

  const handleAddNote = async (content) => {
    if (!content || (typeof content === 'string' && !content.trim())) return;
    const targetProjId = primaryProjectId || selectedProjectIds[0];
    const targetUid = selectedUserId || currentUser?.id;

    if (!targetProjId) {
      toast.error(t('dailyStandup.selectProject'));
      return;
    }

    setIsNoteUpdating(true);

    // Optimistic Update
    const newNote = { id: `temp-${Date.now()}`, note: content, created_at: new Date().toISOString(), project_id: targetProjId };
    let newNotes;
    if (displayNotes.length > 0 && Array.isArray(displayNotes[0]?.notes)) {
      const groupIndex = displayNotes.findIndex(g => String(g.project_id) === String(targetProjId));
      if (groupIndex >= 0) {
        newNotes = [...displayNotes];
        newNotes[groupIndex] = { ...newNotes[groupIndex], notes: [...newNotes[groupIndex].notes, newNote] };
      } else {
        newNotes = [...displayNotes, { project_id: targetProjId, project_name: t('common.general'), notes: [newNote] }];
      }
    } else {
      newNotes = [...displayNotes, newNote];
    }
    setOptimisticNotes(newNotes);

    try {
      const resultAction = await dispatch(
        addDailyStandupNote({
          projectId: targetProjId,
          targetUserId: targetUid,
          notes: content,
        })
      );

      if (addDailyStandupNote.fulfilled.match(resultAction)) {
        await dispatch(
          fetchDailyStandupLog({
            projectIds: selectedProjectIds.join(','),
            userId: targetUid,
            worklogFilter,
          })
        );
        toast.success(t('dailyStandup.logStandup.addNoteSuccess'));
      } else {
        toast.error(
          resultAction.payload || t('dailyStandup.logStandup.addNoteError')
        );
        setOptimisticNotes(null); // Revert on failure
      }
    } catch (err) {
      toast.error(err.message || t('dailyStandup.logStandup.addNoteError'));
      setOptimisticNotes(null); // Revert on failure
    } finally {
      setIsNoteUpdating(false);
    }
  };

  const handleEditNote = async (note, content) => {
    if (!content || (typeof content === 'string' && !content.trim())) return;
    const targetProjId = primaryProjectId || selectedProjectIds[0];
    const targetUid = selectedUserId || currentUser?.id;

    if (!targetProjId) {
      toast.error(t('dailyStandup.selectProject'));
      return;
    }

    const noteId = typeof note === 'object' ? (note?.id || note?.note_id || note?._id) : note;

    setIsNoteUpdating(true);

    // Optimistic Update
    if (noteId) {
      const newNotes = displayNotes.map(item => {
        if (Array.isArray(item.notes)) {
          return {
            ...item,
            notes: item.notes.map(n => String(n.id) === String(noteId) || String(n.note_id) === String(noteId) ? { ...n, note: content, notes: content, content } : n)
          };
        } else {
          return String(item.id) === String(noteId) || String(item.note_id) === String(noteId) ? { ...item, note: content, notes: content, content } : item;
        }
      });
      setOptimisticNotes(newNotes);
    } else {
      // If no noteId, it's treated as add. We use handleAddNote logic for optimism
      // but to keep it simple we'll just not optimistically update an edit that adds (rare).
    }

    try {
      const resultAction = noteId
        ? await dispatch(
            updateDailyStandupNote({
              noteId,
              projectId: targetProjId,
              targetUserId: targetUid,
              notes: content,
            })
          )
        : await dispatch(
            addDailyStandupNote({
              projectId: targetProjId,
              targetUserId: targetUid,
              notes: content,
            })
          );

      const isFulfilled = noteId
        ? updateDailyStandupNote.fulfilled.match(resultAction)
        : addDailyStandupNote.fulfilled.match(resultAction);

      if (isFulfilled) {
        await dispatch(
          fetchDailyStandupLog({
            projectIds: selectedProjectIds.join(','),
            userId: targetUid,
            worklogFilter,
          })
        );
        toast.success(t('dailyStandup.logStandup.updateNoteSuccess'));
      } else {
        toast.error(
          resultAction.payload || t('dailyStandup.logStandup.addNoteError')
        );
        setOptimisticNotes(null); // Revert on failure
      }
    } catch (err) {
      toast.error(err.message || t('dailyStandup.logStandup.addNoteError'));
      setOptimisticNotes(null); // Revert on failure
    } finally {
      setIsNoteUpdating(false);
    }
  };

  const handleDeleteNote = async (note) => {
    const noteId = typeof note === 'object' ? (note?.id || note?.note_id || note?._id) : note;
    if (!noteId) {
      toast.error(t('dailyStandup.logStandup.invalidNote'));
      return;
    }

    const targetUid = selectedUserId || currentUser?.id;

    setIsNoteUpdating(true);

    // Optimistic Update
    const newNotes = displayNotes.map(item => {
      if (Array.isArray(item.notes)) {
        return {
          ...item,
          notes: item.notes.filter(n => String(n.id) !== String(noteId) && String(n.note_id) !== String(noteId))
        };
      }
      return item;
    }).filter(item => {
      if (Array.isArray(item.notes)) return item.notes.length > 0;
      return String(item.id) !== String(noteId) && String(item.note_id) !== String(noteId);
    });
    setOptimisticNotes(newNotes);

    try {
      const resultAction = await dispatch(deleteDailyStandupNote(noteId));

      if (deleteDailyStandupNote.fulfilled.match(resultAction)) {
        await dispatch(
          fetchDailyStandupLog({
            projectIds: selectedProjectIds.join(','),
            userId: targetUid,
            worklogFilter,
          })
        );
        toast.success(t('dailyStandup.logStandup.deleteNoteSuccess'));
      } else {
        toast.error(
          resultAction.payload || t('dailyStandup.logStandup.deleteNoteError')
        );
        setOptimisticNotes(null); // Revert on failure
      }
    } catch (err) {
      toast.error(err.message || t('dailyStandup.logStandup.deleteNoteError'));
      setOptimisticNotes(null); // Revert on failure
    } finally {
      setIsNoteUpdating(false);
    }
  };

  if (teamMembersLoading || projectDropdownsByUserLoading || (isBusy && !data)) {
    return <LogStandupSkeleton showTeamSelector={canViewTeamSelector} />;
  }

  const loadError = projectDropdownsByUserError || teamMembersError || standupLogError;

  if (loadError) {
    return (
      <Box className="log-standup-tab-container">
        {canViewTeamSelector && (
          <DeveloperSelector
            teamMembers={visibleTeamMembers}
            selectedUserId={selectedUserId}
            onSelectUser={setSelectedUserId}
            disabled={isRefreshingOrSwitching}
          />
        )}
        <Box className="standup-no-data-state">
          <Typography className="standup-no-data-title">{t('dailyStandup.noData.title')}</Typography>
          <Typography className="standup-no-data-description">
            {typeof loadError === 'string' ? loadError : t('dailyStandup.noData.description')}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!primaryProjectId) {
    return (
      <Box className="log-standup-tab-container empty-state-padding">
        <Typography>{t('dailyStandup.selectProject')}</Typography>
      </Box>
    );
  }

  return (
    <Box className="log-standup-tab-container">
      {canViewTeamSelector && (
        <DeveloperSelector
          teamMembers={visibleTeamMembers}
          selectedUserId={selectedUserId}
          onSelectUser={setSelectedUserId}
          disabled={isRefreshingOrSwitching}
        />
      )}

      {isRefreshingOrSwitching ? (
        <Box className="skeleton-wrapper">
          <LogStandupContentSkeleton />
        </Box>
      ) : (
        <Box className="content-sections">
          <DayPlanTable
            title={t('dailyStandup.logStandup.yesterdaysPlan')}
            projects={yesterdayProjects}
            emptyMessage={t('dailyStandup.logStandup.noYesterdaysPlan')}
            isToday={false}
          />

          <DayPlanTable
            title={t('dailyStandup.logStandup.todaysPlan')}
            projects={todayProjects}
            emptyMessage={t('dailyStandup.logStandup.noTodaysPlan')}
            isToday
          />

          <WorklogStrip
            worklogData={worklogData}
            filterMode={worklogFilter}
            onFilterChange={setWorklogFilter}
            loading={standupLogLoading && !isNoteUpdating}
          />

          <BlockersRisks blockers={flatBlockers} disableGrouping />

          <AdditionalNotes
            notes={displayNotes}
            isEditable={isViewingOwnStandup}
            onAddNote={handleAddNote}
            onEditNote={handleEditNote}
            onDeleteNote={handleDeleteNote}
            groupByProject
            disableAddNote={selectedProjectIds.length > 1}
          />
        </Box>
      )}
    </Box>
  );
};

export default LogStandupTab;
